import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type {
  UseDataSourceOptions,
  DataSourceResult,
  DataResponse,
  FetchParams,
  TableStateParams,
} from './useDataSource.types';

const DEFAULT_PAGE_SIZE = 20;
const DEFAULT_STALE_TIME = 30_000;

function mapTableStateToParams(tableState?: TableStateParams): Partial<FetchParams> {
  if (!tableState) return {};

  return {
    sorting: tableState.sorting,
    filters: tableState.filters,
    grouping: tableState.grouping,
    ...(tableState.pagination && {
      page: tableState.pagination.pageIndex + 1,
      pageSize: tableState.pagination.pageSize,
    }),
  };
}

export function useDataSource<T>({
  mode,
  queryKey,
  fetchFn,
  tableState,
  enabled = true,
  defaultPageSize = DEFAULT_PAGE_SIZE,
  staleTime = DEFAULT_STALE_TIME,
  pageIndex: externalPageIndex,
  pageSize: externalPageSize,
}: UseDataSourceOptions<T>): DataSourceResult<T> {
  // Internal state — used only when no external pagination is provided (backward compat)
  const [internalPage, setInternalPage] = useState(1);

  // External pagination takes precedence over internal state
  const isExternallyControlled = externalPageIndex !== undefined;
  const effectivePage = isExternallyControlled ? externalPageIndex + 1 : internalPage; // 1-based for API
  const effectivePageSize = externalPageSize ?? defaultPageSize;

  // Build query key based on mode
  const finalQueryKey = (() => {
    const params = mapTableStateToParams(tableState);
    if (mode === 'pagination') {
      return [...queryKey, { ...params, page: effectivePage, pageSize: effectivePageSize }];
    }
    if (mode === 'infinite') {
      return [...queryKey, { ...params, pageSize: effectivePageSize }];
    }
    return queryKey;
  })();

  const {
    data: queryData,
    isLoading: queryIsLoading,
    isError: queryIsError,
    isFetching: queryIsFetching,
    error: queryError,
    refetch,
  } = useQuery<DataResponse<T>, Error>({
    queryKey: finalQueryKey as readonly unknown[],
    queryFn: () =>
      fetchFn({
        ...mapTableStateToParams(tableState),
        page: effectivePage,
        pageSize: effectivePageSize,
      } as FetchParams),
    enabled: mode !== 'static' && enabled,
    staleTime: mode === 'static' ? Infinity : staleTime,
    // Prevents content flash when changing pages
    placeholderData: mode === 'pagination' ? keepPreviousData : undefined,
  });

  const data: T[] = (() => {
    if (mode === 'static') {
      return [];
    }
    return queryData?.items ?? [];
  })();

  const setPage = (newPage: number) => {
    setInternalPage(newPage);
  };

  const totalPages = queryData?.totalPages
    ? Math.ceil(queryData.totalPages)
    : queryData?.total
      ? Math.ceil(queryData.total / effectivePageSize)
      : undefined;

  // Minimal table-like object for compatibility
  const table = {
    getRowModel: () => ({ rows: [] }),
    getState: () => ({
      sorting: tableState?.sorting ?? [],
      grouping: tableState?.grouping ?? [],
    }),
    getFlatHeaders: () => [],
  };

  return {
    mode,
    table: table as unknown as DataSourceResult<T>['table'],
    data,
    isLoading: mode === 'static' ? false : queryIsLoading,
    isError: mode === 'static' ? false : queryIsError,
    error: mode === 'static' ? null : queryError,
    isFetching: mode === 'static' ? false : queryIsFetching,

    ...(mode === 'pagination' && {
      page: effectivePage,
      totalPages,
      totalCount: queryData?.total,
      // Only expose setPage when in internal-state mode (backward compat)
      setPage: isExternallyControlled ? undefined : setPage,
    }),

    ...(mode === 'infinite' && {
      hasNextPage: false,
      fetchNextPage: () => {},
    }),

    refetch,
  };
}
